import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngxs/store';
import { FileType, FileTypeForDisplaying } from 'src/app/inventory/interfaces/file-type.enum';
import { IFilesSignatureReceivedEvent } from 'src/app/inventory/interfaces/files-signature-received-event.interface';
import { FieldType } from '../../interfaces/field-type.enum';
import { GenericTableColumn } from '../../interfaces/genetic-table-column';
import { IOptionItem } from '../../interfaces/option-item.interface';
import { ISaveAttachedFileEvent } from '../../interfaces/save-attached-file-event.interface';
import { FileExtensionPipe } from '../../pipes/file-extension/file-extension.pipe';
import { FileService } from '../../services/file-service';
import { SetLoading } from '../../states/orders/orders.actions';
import { IFileUploadObject } from '../file-uploader/file-upload-object.interface';
import { IAttachedFileRow } from './attached-file-row.interface';

@Component({
  selector: 'app-file-attachment',
  templateUrl: './file-attachment.component.html',
  styleUrls: ['./file-attachment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileAttachmentComponent implements OnChanges {

  @Input() files: IFileUploadObject[];
  @Input() defaultReceiveAttachmentDialog: boolean = true;
  @Input() header: string = 'Attached associated order files';
  @Input() headerClasses: string;
  @Input() fileTypes: IOptionItem[];
  @Input() requiresSignature: boolean;
  @Input() csosControl: FormControl;
  @Input() loadFileBeforeReSigning: boolean = true; //determine whether we should load the file using a pre-signed URL before signing it again as part of the update operation
  
  @Output() downloadFile: EventEmitter<IFileUploadObject> = new EventEmitter();
  @Output() addFiles: EventEmitter<void> = new EventEmitter();
  @Output() editFiles: EventEmitter<void> = new EventEmitter();
  //for default dialog
  @Output() saveFiles: EventEmitter<ISaveAttachedFileEvent> = new EventEmitter();
  @Input() tableHeight: number = 134;

  isEditMode: boolean;
  showAttachFilesDialog: boolean;

  editableFile: IFileUploadObject;

  tableData: IAttachedFileRow[];

  font500 = {'font-weight': 500};
  width12 = {width: '12%'};
  columns: GenericTableColumn<IAttachedFileRow>[] = [
    new GenericTableColumn<IAttachedFileRow>({ header: 'File Name', field: 'name', 
      style: {...this.font500, width: '40%'}
    }),
    new GenericTableColumn<IAttachedFileRow>({ header: 'File Type', field: 'type', style: {...this.font500, width: '15%'}}),
    new GenericTableColumn<IAttachedFileRow>({ header: 'User', field: 'user',  style: {...this.font500, ...this.width12}}),
    new GenericTableColumn<IAttachedFileRow>({ header: 'Uploaded', field: 'createDate', style: {...this.font500, ...this.width12}, fieldType: FieldType.DateTime}),
    new GenericTableColumn<IAttachedFileRow>({ header: 'Updated', field: 'lastModificationDate', style: {...this.font500, ...this.width12}, fieldType: FieldType.DateTime}),
  ];
  /**
   *
   */
  constructor(
    private fileExtensionPipe: FileExtensionPipe, 
    private store: Store,
    private fileService: FileService
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
   this.tableData = this.files?.map(x => ({
    id: x.id,
    name: this.fileExtensionPipe.transform(x.name), 
    type: x.type !== FileType.Other ? FileTypeForDisplaying[x.type] : x.otherType,
    user: x.user?.firstName && x.user?.lastName ? x.user?.firstName + ' ' + x.user?.lastName[0] + '.' : x.user,
    createDate: x.createDate,
    lastModificationDate: x.lastModificationDate,
    editControl: 'Edit',
    }));
  }

  openAttachFilesDialog(id: string, isEdit: boolean): void {
    if (this.defaultReceiveAttachmentDialog) {
      this.editableFile = this.files?.find(x => x.id === id);
      this.showAttachFilesDialog = true;
      this.isEditMode = isEdit;
    } else {
      this.isEditMode ? this.editFiles.emit() : this.addFiles.emit();
    }
  }
  //edit a file at a time
  async saveAttachmentsList(event: IFilesSignatureReceivedEvent) {
    if (this.isEditMode) {
     if (!event.attachments) {
      this.showAttachFilesDialog = false;
      return;
     }
     const isReplaced = this.editableFile.selectedFile !== event.attachments[0].selectedFile;
     let updatedFiles = this.files.map(file => ({ ...file }));
     const idx = this.files.findIndex(x => x.id === this.editableFile.id);
     if (!isNaN(idx) && idx > -1 && event.attachments[0]) {
      const oldName = updatedFiles[idx].name;
      const newName = event.attachments[0].name;
      updatedFiles[idx].name = newName;
      updatedFiles[idx].type = event.attachments[0].type;
      updatedFiles[idx].otherType = event.attachments[0].otherType;
      updatedFiles[idx].isReplaced = isReplaced;
      updatedFiles[idx].selectedFile = event.attachments[0].selectedFile;
    if (this.loadFileBeforeReSigning && this.requiresSignature) {
        const operationResult = await this.fileService.getFileWithMetadata(updatedFiles[idx].id, oldName, newName, updatedFiles[idx].selectedFile);
        updatedFiles[idx].selectedFile = operationResult.file;
        updatedFiles[idx].downloadUrl = operationResult?.url;
        updatedFiles[idx].isReplaced = true; //server will return a new pre-assigned url
        updatedFiles[idx].isSigned = operationResult.isSigned && !isReplaced;
      }
     } //file was removed
     else if (!isNaN(idx) && event.attachments?.length === 0) {
      updatedFiles.splice(idx, 1);
     }
     
     this.saveFiles.emit({attachments: updatedFiles, isEditMode: true, signature: event.signature});
    } else {
      this.saveFiles.emit({attachments: event.attachments, isEditMode: false, signature: event.signature});
    }
    this.store.dispatch(new SetLoading(false));
    this.showAttachFilesDialog = false;
  }


  downloadFileClicked(index: number): void {
    this.downloadFile.emit(this.files[index]);
  }

  saveSorted(sortedData: IAttachedFileRow[]): void {
    //sort files based on the order of files in table
    this.files = this.sortFilesBasedOnSortedTable( [...this.files], sortedData?.map(x => x.id));
  }

  sortFilesBasedOnSortedTable(collectionA, collectionB): IFileUploadObject[] {
    const idIndexMap = collectionB.reduce((acc, id, index) => {
        acc[id] = index;
        return acc;
    }, {});
    return collectionA.sort((a, b) => {
        return idIndexMap[a.id] - idIndexMap[b.id];
    });
  }

  closeAttachFilesDialog(): void {
    this.csosControl?.disable();
    this.showAttachFilesDialog = false;
  }
  
}
